import { useState } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ItemContentsMobile } from './ItemContents';
import { ItemRecipientMobile } from './ItemRecipient';
import { ItemServiceMobile } from './ItemService';
import { EditType } from './EditType';
import { P2GAlert, P2GBox, P2GButton } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { P2GCheckBoxLegacy } from '@parcel2go/core-ui';
import { ItemCustomsMobile } from './ItemCustoms';
import { ItemActionType } from './SetMarketplaceAction';
import { ItemExtrasMobile } from './ItemExtras';
import { RequiresCustoms, RequiresTaxIdsButNotCustoms } from '../../../lib/helpers/SmartSendValidation';
import SmartSendItemActiveDrop from './SmartSendItemActiveDrop';
import ItemErrorSummary from './ItemErrorSummary';
import { DimensionAndWeightDisplay } from '../DimensionDisplay';
import { P2GSidePanel } from '~/components/sidepanel';
import { IsWindsor } from '~/apps/smartsend/lib/helpers/IsWindsor';
import { GetCollatedCustomerReferenceNumbers } from './OrderReferenceNumberUtils';
import ItemTotal from './ItemTotal';
import { Truncate } from '../Truncate';
import ECommerceLogo from '~/apps/account-linking/components/ECommerceLogo';
import ClosestPickUpDropShop from './ClosestPickUpShop';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

interface props {
  item: SmartSendItemApiModel;
  toggleSelect: (val: boolean) => void;
  removeTag: (tag: string) => void;
  saveTags: (tags: string[]) => void;
  edit: (type: EditType) => void;
  action: (type: ItemActionType) => void;
  isReturn: boolean;
  saveForLater: (select: boolean) => void;
  deleteItem: () => void;
  book: () => void;
}

export default function SmartSendItemActiveMobile(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [showActions, setShowActions] = useState(false);
  const sourceId = props.item.MarketplaceOrderLines?.[0]?.Source?.Id;
  const refs = GetCollatedCustomerReferenceNumbers(props.item.MarketplaceOrderLines);
  const canBook = !!props.item.State?.validation?.Validated;
  const requiresCustomsForItem = RequiresCustoms(props.item.State?.validation);
  const requiresTaxIdsForItem = !requiresCustomsForItem && RequiresTaxIdsButNotCustoms(props.item.State?.validation);

  const errors = (match: string[]): string[] | undefined => {
    return props.item.State?.validation?.Errors?.filter((x) => !!match.find((m) => x.indexOf(m) === 0));
  };

  const editCustoms = () => {
    props.edit && props.edit('CUSTOMS');
  };

  const edit = (type: EditType) => {
    props.edit && props.edit(type);
  };

  const action = (type: ItemActionType) => {
    props.action && props.action(type);
  };

  return (
    <P2GBox p={1}>
      <P2GBox p={1} display="flex" gap={1} flexDirection="row" alignItems="center" justifyContent="space-between">
        {sourceId && <ECommerceLogo integration={sourceId} style={{ width: '80px' }} />}
        <P2GText variant="subtitle2" component="div" flexGrow={1}>
          <Truncate>{refs}</Truncate>
        </P2GText>
        <P2GCheckBoxLegacy
          labelPlacement="start"
          id="Chk"
          onChange={props.toggleSelect}
          checked={props.item.State?.selected}
          label={
            props.item.State?.validation?.Validated ? (
              <P2GText variant="body2">
                <strong style={{ color: '#629833' }}>Ready</strong>
              </P2GText>
            ) : (
              <P2GText variant="body2" colour="error">
                <strong>Not Ready</strong>
              </P2GText>
            )
          }
          variant="primary"
          error={false}
        />
      </P2GBox>
      <P2GBox p={1} sx={(t) => ({ backgroundColor: t.palette.grey.A200, borderRadius: t.spacing(1) })}>
        <ItemRecipientMobile
          edit={edit}
          customer={props.isReturn ? props.item.Sender : props.item.Recipient}
          errors={errors(['ADDRESS-'])}
          variant="body2"
        />
      </P2GBox>
      <P2GBox p={1}>
        <ItemContentsMobile edit={edit} item={props.item} errors={errors(['ITEM-'])} variant="body2" />
      </P2GBox>
      <P2GBox p={1}>
        <ItemErrorSummary edit={edit} variant="body2" item={props.item} sm={5} component="Box">
          <ItemServiceMobile
            itemId={props.item.Id}
            edit={edit}
            service={props.item.State?.validation?.QuoteState?.Quote?.Service || props.item.Service}
            errors={errors(['SERVICE-', 'EXTRAS-'])}
            variant="body2"
          >
            <DimensionAndWeightDisplay dimensions={props.item.PackageDimensions} variant="body2" />
          </ItemServiceMobile>
          <P2GBox pt={1}>
            <ItemExtrasMobile variant="body2" extras={props.item.Extras} edit={() => props.edit('EXTRAS')} />
          </P2GBox>
          {requiresCustomsForItem && (
            <P2GBox pt={1}>
              <ItemCustomsMobile
                edit={editCustoms}
                variant="body2"
                label={
                  IsWindsor({ Sender: props.item.Sender, Recipient: props.item.Recipient } as SmartSendItemApiModel)
                    ? 'Additional Information'
                    : null
                }
              />
            </P2GBox>
          )}
          {requiresTaxIdsForItem && (
            <P2GBox pt={1}>
              <ItemCustomsMobile
                edit={editCustoms}
                variant="body2"
                label={t('TAXT_IDS_LINK', { defaultValue: 'Tax Ids' })}
              />
            </P2GBox>
          )}
        </ItemErrorSummary>
      </P2GBox>
      {props.item.State.validation?.QuoteState?.Quote && (
        <P2GBox p={1}>
          <P2GBox
            textAlign="center"
            sx={(t) => ({ pt: 2, pb: 2, background: t.palette.grey[100], borderRadius: t.spacing(1) })}
          >
            <ItemTotal item={props.item} quote={props.item.State.validation?.QuoteState?.Quote} variant="subtitle2" />
          </P2GBox>
        </P2GBox>
      )}
      <ClosestPickUpDropShop
        item={props.item}
        isXs={true}
        wrapper={(x) => (
          <P2GBox p={1}>
            <P2GAlert severity="warning">{x}</P2GAlert>
          </P2GBox>
        )}
      />
      <P2GBox p={1} textAlign="center" display="flex" gap={1} flexDirection="row" flexWrap="nowrap">
        <P2GButton variant="tertiary" fullWidth onClick={() => setShowActions(true)}>
          Actions
        </P2GButton>
        {canBook && props.book && (
          <P2GButton variant="secondary" fullWidth onClick={props.book}>
            Book
          </P2GButton>
        )}
      </P2GBox>
      <P2GSidePanel open={showActions} onClose={() => setShowActions(false)} anchor="bottom">
        <SmartSendItemActiveDrop
          edit={edit}
          action={action}
          saveForLater={props.saveForLater}
          deleteItem={props.deleteItem}
          book={props.book}
          canBook={canBook}
          direction="column"
          isXs={true}
        />
      </P2GSidePanel>
    </P2GBox>
  );
}
